import { cva, type VariantProps } from "class-variance-authority";
import { CircleAlert, CircleCheck, Info, X } from "lucide-react";
import type { Ref } from "react";
import React from "react";

import { cn } from "@krea/shared/utils";

import { Button } from "../Button";

const alertVariants = cva(
  "relative w-full rounded-xl border px-4 py-3 text-sm",
  {
    variants: {
      variant: {
        info: "border-gray-200",
        infoAlert: "border-primary",
        warning: "border-orange",
        critical: "border-red",
        success: "border-mint-500",
        plain: "",
      },
    },
    defaultVariants: {
      variant: "info",
    },
  },
);

type AlertVariants = VariantProps<typeof alertVariants>;

export type AlertProps = Omit<AlertVariants, "variant"> & {
  variant: NonNullable<Required<AlertVariants>["variant"]>;
  rightElement?: React.ReactNode;
  leftElement?: React.ReactNode;
  isClosable?: boolean;
  mainContentClassName?: string;
  extraContent?: React.ReactNode;
  centeredIcon?: boolean;
};

type AlertOwnProps = React.HTMLAttributes<HTMLDivElement> &
  AlertProps & { ref?: Ref<HTMLDivElement> };

// Alert carries `.Title` / `.Description` statics, so it needs a named type rather
// than plain inference. It used to extend ForwardRefExoticComponent; with ref as a
// plain prop it is just a call signature plus those statics.
interface AlertComponent {
  (props: AlertOwnProps): React.ReactNode;
  displayName?: string;
  Title: typeof AlertTitle;
  Description: typeof AlertDescription;
}

interface IIconProps {
  variant: NonNullable<Required<AlertVariants>["variant"]>;
  className?: string;
}

const IconFromVariant = ({ variant, className }: IIconProps) => {
  switch (variant) {
    case "info":
      return (
        <div
          className={cn("flex w-min justify-center items-center", className)}
        >
          <div className="mt-[0.5px] h-5">
            <Info
              className="h-5 w-5 text-primary"
              size={20}
              strokeWidth={2.5}
            />
          </div>
        </div>
      );
    case "warning":
      return (
        <div
          className={cn("flex w-min justify-center items-center", className)}
        >
          <div className="mt-[0.5px] h-5 rounded-full bg-orange-100">
            <CircleAlert
              className="h-5 w-5 text-orange [&>circle]:stroke-none"
              size={20}
              stroke="white"
              strokeWidth={2.5}
              fill="currentColor"
            />
          </div>
        </div>
      );
    case "infoAlert":
      return (
        <div
          className={cn("flex w-min justify-center items-center", className)}
        >
          <div className="mt-[0.5px] h-5 rounded-full ">
            <CircleAlert className="h-5 w-5 text-primary" size={20} />
          </div>
        </div>
      );
    case "critical":
      return (
        <div
          className={cn("flex w-min justify-center items-center", className)}
        >
          <div className="mt-[0.5px] h-5 rounded-full bg-red-100">
            <CircleAlert
              className="h-5 w-5 text-red [&>circle]:stroke-none"
              size={20}
              stroke="white"
              strokeWidth={2.5}
              fill="currentColor"
            />
          </div>
        </div>
      );
    case "success":
      return (
        <div
          className={cn("flex w-min justify-center items-center", className)}
        >
          <div className="mt-[0.5px] h-5 rounded-full bg-mint-100">
            <CircleCheck
              className="h-5 w-5 text-mint-500"
              size={20}
              stroke="white"
              strokeWidth={2.5}
              fill="currentColor"
            />
          </div>
        </div>
      );
  }
};

const Alert = (({
  className,
  variant = "info",
  isClosable = false,
  rightElement,
  leftElement,
  mainContentClassName,
  extraContent,
  centeredIcon = false,
  ref,
  ...props
}: AlertOwnProps) => {
  const [isClosed, setIsClosed] = React.useState(false);

  return (
    <div
      ref={ref}
      role="alert"
      className={cn(
        alertVariants({ variant }),
        "transition-all duration-500 ease-in-out fade-in relative",
        {
          "opacity-0 invisible max-h-0": isClosed,
          "opacity-100 visible max-h-[9999px] has-[*]:my-4": !isClosed,
        },
        "flex justify-between",
        className,
      )}
      {...props}
    >
      <div className="flex w-full flex-col">
        <div className={cn("flex w-full", mainContentClassName)}>
          <div
            className={cn("flex w-full", {
              "items-top": !leftElement,
              "items-center": leftElement,
            })}
          >
            {leftElement ? (
              leftElement
            ) : (
              <IconFromVariant
                variant={variant}
                className={cn({
                  "items-center": centeredIcon,
                  "items-top": !centeredIcon,
                })}
              />
            )}
            <div
              className={cn("ml-3 w-full self-center", {
                "ml-0": variant === "plain" && !leftElement,
              })}
            >
              {props.children}
            </div>
          </div>
          {rightElement}
        </div>
        {extraContent && <div className="mt-2">{extraContent}</div>}
      </div>
      {isClosable && (
        <div className="flex items-center">
          <Button variant="ghost" size="icon" onClick={() => setIsClosed(true)}>
            <X
              strokeWidth={3}
              className={cn("h-4 w-4 text-gray-500", {
                "text-orange": variant === "warning",
                "text-red": variant === "critical",
                "text-mint-500": variant === "success",
              })}
            />
          </Button>
        </div>
      )}
    </div>
  );
}) as AlertComponent;
Alert.displayName = "Alert";

const AlertTitle = ({
  className,
  children,
  ref,
  ...props
}: React.HTMLAttributes<HTMLHeadingElement> & {
  ref?: Ref<HTMLHeadingElement>;
}) => (
  <h5
    ref={ref}
    className={cn(
      "-mt-px mb-2 last:mb-0 lg:mt-[-2px] leading-none tracking-tight text-base-strong",
      className,
    )}
    {...props}
  >
    {children}
  </h5>
);
AlertTitle.displayName = "AlertTitle";

const AlertDescription = ({
  className,
  ref,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & {
  ref?: Ref<HTMLDivElement>;
}) => (
  <div
    ref={ref}
    className={cn("text-sm [&_p]:leading-relaxed font-normal", className)}
    {...props}
  />
);
AlertDescription.displayName = "AlertDescription";

Alert.Title = AlertTitle;
Alert.Description = AlertDescription;

export { Alert, AlertDescription, AlertTitle };
